import React, { useState } from 'react';
import { useSubjectLanguage } from '@features/user';
import { Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, capitalize, Popover, Typography } from '@mui/material';

// Processing fee percentage constant
export const PAYMENT_PROCESSING_FEE = '2.9% + $0.30';

export const PaymentInfoContent: React.FC = () => {
  const subjectLanguage = useSubjectLanguage();

  return (
    <>
      <Typography variant="body2" component="div" sx={{ mb: 1 }}>
        <strong>How {subjectLanguage} payments work:</strong>
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 1 }}>
        When you request a payment, your {subjectLanguage} will receive a secure link via{' '}
        <strong>SMS</strong> to pay online. Payments are processed through <strong>Stripe</strong>,
        a secure platform used by thousands of healthcare providers.
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 1 }}>
        You&apos;ll be notified once the payment is completed in JotPsych, and all requests will be{' '}
        <strong>tracked within your Stripe account</strong>. Funds are deposited directly into your
        connected Stripe account.
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 1 }}>
        A <strong>processing fee</strong> of {PAYMENT_PROCESSING_FEE} will be deducted from each
        transaction. This fee covers secure Stripe payment processing and transaction costs.
      </Typography>
      <Typography variant="body2" component="div">
        You&apos;ll be prompted to create a <strong>Stripe account</strong> the first time you
        request a payment. This only takes a few minutes and ensures funds are deposited securely
        into your bank account.
      </Typography>
    </>
  );
};

export const PaymentInfoAlert: React.FC = () => (
  <Alert severity="info" sx={{ mt: 3 }}>
    <PaymentInfoContent />
  </Alert>
);

export const PaymentInfoPopover: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const subjectLanguage = useSubjectLanguage();
  const SubjectLanguage = capitalize(subjectLanguage);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<InfoIcon />}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{
          borderColor: 'info.main',
          color: 'white',
          backgroundColor: 'info.main',
          borderRadius: 2,
          px: 2,
          py: 1,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'info.dark',
            color: 'white',
            borderColor: 'info.dark',
          },
        }}
      >
        How {SubjectLanguage} Payments Work
      </Button>
      <Popover
        id="payment-info-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              ml: -2, // Move 16px more to the left
            },
          },
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <PaymentInfoContent />
        </Box>
      </Popover>
    </>
  );
};
